// src/pages/tenant/TenantDashboard.jsx
import { useEffect, useState } from 'react'
import { useTenant } from '../../context/TenantContext'
import { tenantAdminApi as svc } from '../../api/services'

export function TenantDashboard() {
  const { tenant } = useTenant()
  const [data, setData] = useState(null)
  const primary = tenant.primary_color || '#FF3D7F'

  useEffect(() => { svc.dashboard().then(r => setData(r.data?.data ?? r.data)) }, [])

  if (!data) return <div style={{ color: '#9CA3AF', padding: 40, textAlign: 'center' }}>Loading…</div>

  const StatCard = ({ value, label, sub }) => (
    <div style={{ background: '#fff', borderRadius: 16, border: '1.5px solid #F0F0F0', padding: '20px 24px' }}>
      <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: '1.8rem', color: primary }}>{value}</div>
      <div style={{ fontWeight: 700, fontSize: '0.85rem', color: '#1A1A2E', marginTop: 4 }}>{label}</div>
      {sub && <div style={{ fontSize: '0.75rem', color: '#9CA3AF', marginTop: 2 }}>{sub}</div>}
    </div>
  )

  const LimitBar = ({ used, max, label }) => {
    const pct = max ? Math.min((used / max) * 100, 100) : 0
    const color = pct > 90 ? '#DC2626' : pct > 70 ? '#F59E0B' : primary
    return (
      <div style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: '0.82rem', fontWeight: 600, color: '#374151' }}>
          <span>{label}</span>
          <span style={{ color }}>{used}{max ? ` / ${max}` : ' (unlimited)'}</span>
        </div>
        <div style={{ height: 6, background: '#F0F0F0', borderRadius: 3 }}>
          <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 3, transition: 'width 0.6s ease' }} />
        </div>
      </div>
    )
  }

  return (
    <div>
      <h1 style={{ fontFamily: 'Playfair Display, serif', fontWeight: 800, fontSize: '1.8rem', color: '#1A1A2E', margin: '0 0 6px' }}>{tenant.brand_name} Dashboard</h1>
      <p style={{ color: '#9CA3AF', margin: '0 0 28px', fontSize: '0.88rem' }}>
        <span style={{ background: `${primary}15`, color: primary, padding: '2px 10px', borderRadius: 12, fontWeight: 700, marginRight: 8 }}>{data.plan?.toUpperCase()}</span>
        {data.status === 'trial' && <span style={{ background: '#FFF7ED', color: '#D97706', padding: '2px 10px', borderRadius: 12, fontWeight: 700 }}>Trial — ends {new Date(data.trial_ends_at).toLocaleDateString()}</span>}
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16, marginBottom: 28 }}>
        <StatCard value={data.stats?.suppliers?.toLocaleString()} label="Suppliers" />
        <StatCard value={data.stats?.products?.toLocaleString()} label="Products Listed" />
        <StatCard value={data.stats?.orders?.toLocaleString()} label="Total Orders" />
        <StatCard value={`₹${(data.stats?.gmv / 100000).toFixed(1)}L`} label="Platform GMV" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        <div style={{ background: '#fff', borderRadius: 16, border: '1.5px solid #F0F0F0', padding: '24px' }}>
          <h3 style={{ fontWeight: 700, fontSize: '0.9rem', color: '#1A1A2E', margin: '0 0 20px' }}>Plan Limits</h3>
          <LimitBar used={data.limits?.suppliers_used} max={data.limits?.suppliers_max} label="Suppliers" />
          <LimitBar used={data.limits?.products_used}  max={data.limits?.products_max}  label="Products" />
        </div>
        <div style={{ background: '#fff', borderRadius: 16, border: '1.5px solid #F0F0F0', padding: '24px' }}>
          <h3 style={{ fontWeight: 700, fontSize: '0.9rem', color: '#1A1A2E', margin: '0 0 16px' }}>Quick Links</h3>
          {[['🎨 Customise Branding', '/tenant/branding'], ['🌐 Manage Domains', '/tenant/domains'], ['💳 Billing & Plan', '/tenant/billing'], ['👥 Team Members', '/tenant/team']].map(([label, to]) => (
            <a key={to} href={to} style={{ display: 'block', padding: '8px 0', color: '#374151', textDecoration: 'none', fontSize: '0.85rem', fontWeight: 500, borderBottom: '1px dashed #F0F0F0' }}>
              {label} <span style={{ color: '#D1D5DB', float: 'right' }}>›</span>
            </a>
          ))}
        </div>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// src/pages/tenant/TenantBranding.jsx
export function TenantBranding() {
  const { tenant, setTenant } = useTenant()
  const primary = tenant.primary_color || '#FF3D7F'
  const [form, setForm]     = useState({ brand_name: tenant.brand_name || '', brand_tagline: tenant.brand_tagline || '', primary_color: tenant.primary_color || '#FF3D7F', secondary_color: tenant.secondary_color || '#FF8500', font_heading: tenant.font_heading || 'Playfair Display', font_body: tenant.font_body || 'DM Sans' })
  const [saving, setSaving] = useState(false)
  const [msg, setMsg]       = useState('')

  const save = async () => {
    setSaving(true); setMsg('')
    try {
      const r = await svc.updateBranding(form)
      setTenant(t => ({ ...t, ...form }))
      setMsg('✅ Branding saved!')
    } catch { setMsg('❌ Save failed') }
    finally { setSaving(false) }
  }

  const uploadLogo = async (e, type) => {
    const file = e.target.files[0]; if (!file) return
    const fd = new FormData(); fd.append('logo', file); fd.append('type', type)
    await svc.uploadLogo(fd)
    setMsg('✅ Logo uploaded!')
  }

  const Field = ({ label, children }) => (
    <div style={{ marginBottom: 20 }}>
      <label style={{ display: 'block', fontWeight: 700, fontSize: '0.8rem', color: '#374151', marginBottom: 8 }}>{label}</label>
      {children}
    </div>
  )

  const Input = ({ value, onChange, ...p }) => (
    <input value={value} onChange={onChange} {...p}
      style={{ width: '100%', padding: '10px 14px', border: '1.5px solid #E5E7EB', borderRadius: 10, fontSize: '0.88rem', outline: 'none', boxSizing: 'border-box' }} />
  )

  return (
    <div style={{ maxWidth: 700 }}>
      <h1 style={{ fontFamily: 'Playfair Display, serif', fontWeight: 800, fontSize: '1.6rem', color: '#1A1A2E', margin: '0 0 6px' }}>Branding</h1>
      <p style={{ color: '#9CA3AF', margin: '0 0 28px', fontSize: '0.88rem' }}>Customise your marketplace's look and feel.</p>

      <div style={{ background: '#fff', borderRadius: 16, border: '1.5px solid #F0F0F0', padding: '28px' }}>
        <Field label="Brand Name">
          <Input value={form.brand_name} onChange={e => setForm(f => ({ ...f, brand_name: e.target.value }))} placeholder="Rajasthan Textiles" />
        </Field>
        <Field label="Tagline">
          <Input value={form.brand_tagline} onChange={e => setForm(f => ({ ...f, brand_tagline: e.target.value }))} placeholder="India's finest handloom marketplace" />
        </Field>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <Field label="Primary Colour">
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <input type="color" value={form.primary_color} onChange={e => setForm(f => ({ ...f, primary_color: e.target.value }))} style={{ width: 44, height: 36, border: 'none', cursor: 'pointer', borderRadius: 8 }} />
              <Input value={form.primary_color} onChange={e => setForm(f => ({ ...f, primary_color: e.target.value }))} />
            </div>
          </Field>
          <Field label="Secondary Colour">
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <input type="color" value={form.secondary_color} onChange={e => setForm(f => ({ ...f, secondary_color: e.target.value }))} style={{ width: 44, height: 36, border: 'none', cursor: 'pointer', borderRadius: 8 }} />
              <Input value={form.secondary_color} onChange={e => setForm(f => ({ ...f, secondary_color: e.target.value }))} />
            </div>
          </Field>
        </div>

        <Field label="Logo">
          {tenant.logo_url && <img src={tenant.logo_url} alt="logo" style={{ height: 48, marginBottom: 10, objectFit: 'contain' }} />}
          <input type="file" accept="image/*" onChange={e => uploadLogo(e, 'logo')} style={{ fontSize: '0.82rem' }} />
        </Field>

        <Field label="Favicon">
          <input type="file" accept="image/png,image/x-icon" onChange={e => uploadLogo(e, 'favicon')} style={{ fontSize: '0.82rem' }} />
        </Field>

        {msg && <div style={{ padding: '10px 14px', background: msg.startsWith('✅') ? '#F0FDF4' : '#FEF2F2', borderRadius: 8, fontSize: '0.85rem', marginBottom: 16 }}>{msg}</div>}

        <button onClick={save} disabled={saving} style={{ background: primary, color: '#fff', border: 'none', borderRadius: 10, padding: '11px 28px', fontWeight: 700, fontSize: '0.88rem', cursor: 'pointer' }}>
          {saving ? 'Saving…' : 'Save Branding'}
        </button>
      </div>

      {/* Live preview */}
      <div style={{ background: form.primary_color, borderRadius: 16, padding: '24px', marginTop: 20, textAlign: 'center' }}>
        <div style={{ fontFamily: form.font_heading + ', serif', fontWeight: 800, fontSize: '1.4rem', color: '#fff' }}>{form.brand_name || 'Your Brand'}</div>
        <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.85rem', marginTop: 6 }}>{form.brand_tagline || 'Your tagline here'}</div>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// src/pages/tenant/TenantDomains.jsx
export function TenantDomains() {
  const { tenant } = useTenant()
  const primary = tenant.primary_color || '#FF3D7F'
  const [domains, setDomains] = useState([])
  const [newDomain, setNewDomain] = useState('')
  const [adding, setAdding] = useState(false)
  const [result, setResult] = useState(null)

  useEffect(() => { svc.getDomains().then(r => setDomains(r.data?.data ?? [])) }, [])

  const add = async () => {
    if (!newDomain.trim()) return
    setAdding(true)
    try {
      const r = await svc.addDomain({ domain: newDomain.trim() })
      setResult(r.data?.data)
      svc.getDomains().then(r => setDomains(r.data?.data ?? []))
      setNewDomain('')
    } finally { setAdding(false) }
  }

  const verify = async (id) => {
    const r = await svc.verifyDomain(id)
    alert(r.data?.message || 'Verification attempted')
    svc.getDomains().then(r => setDomains(r.data?.data ?? []))
  }

  return (
    <div style={{ maxWidth: 700 }}>
      <h1 style={{ fontFamily: 'Playfair Display, serif', fontWeight: 800, fontSize: '1.6rem', color: '#1A1A2E', margin: '0 0 6px' }}>Domains</h1>
      <p style={{ color: '#9CA3AF', margin: '0 0 28px', fontSize: '0.88rem' }}>Your default subdomain is always active. Add a custom domain on Growth plan and above.</p>

      {/* Default subdomain */}
      <div style={{ background: '#F0FDF4', border: '1.5px solid #6EE7B7', borderRadius: 14, padding: '16px 20px', marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: '0.9rem', color: '#065F46' }}>{tenant.slug}.fabrinza.com</div>
          <div style={{ fontSize: '0.75rem', color: '#6EE7B7', marginTop: 2 }}>✓ Default subdomain · Always active</div>
        </div>
        <span style={{ background: '#D1FAE5', color: '#065F46', padding: '3px 10px', borderRadius: 8, fontSize: '0.72rem', fontWeight: 700 }}>Primary</span>
      </div>

      {/* Custom domains */}
      {domains.filter(d => !d.is_primary).map(d => (
        <div key={d.id} style={{ background: '#fff', border: '1.5px solid #F0F0F0', borderRadius: 14, padding: '16px 20px', marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: '0.9rem', color: '#1A1A2E' }}>{d.domain}</div>
            <div style={{ fontSize: '0.75rem', color: '#9CA3AF', marginTop: 2 }}>SSL: {d.ssl_status}</div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {!d.is_verified && <button onClick={() => verify(d.id)} style={{ padding: '6px 14px', borderRadius: 8, border: `1px solid ${primary}`, color: primary, background: 'none', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600 }}>Verify DNS</button>}
            <span style={{ padding: '4px 10px', borderRadius: 8, fontSize: '0.72rem', fontWeight: 700, background: d.is_verified ? '#D1FAE5' : '#FEF3C7', color: d.is_verified ? '#065F46' : '#92400E' }}>
              {d.is_verified ? '✓ Verified' : '⏳ Pending'}
            </span>
          </div>
        </div>
      ))}

      {/* DNS instruction */}
      {result?.verification && (
        <div style={{ background: '#F8F9FA', borderRadius: 14, padding: '16px 20px', marginBottom: 16, border: '1px dashed #E5E7EB' }}>
          <div style={{ fontWeight: 700, fontSize: '0.85rem', color: '#1A1A2E', marginBottom: 8 }}>Add this DNS TXT record:</div>
          <div style={{ fontFamily: 'monospace', fontSize: '0.8rem', color: '#374151' }}>
            <div><strong>Name:</strong> {result.verification.name}</div>
            <div><strong>Value:</strong> {result.verification.value}</div>
          </div>
          <div style={{ fontSize: '0.75rem', color: '#9CA3AF', marginTop: 8 }}>{result.verification.note}</div>
        </div>
      )}

      {/* Add domain */}
      {(tenant.plan_config?.custom_domain) && (
        <div style={{ background: '#fff', borderRadius: 14, border: '1.5px solid #F0F0F0', padding: '20px' }}>
          <div style={{ fontWeight: 700, fontSize: '0.85rem', color: '#1A1A2E', marginBottom: 12 }}>Add Custom Domain</div>
          <div style={{ display: 'flex', gap: 10 }}>
            <input value={newDomain} onChange={e => setNewDomain(e.target.value)} placeholder="marketplace.yourbrand.com"
              style={{ flex: 1, padding: '10px 14px', border: '1.5px solid #E5E7EB', borderRadius: 10, fontSize: '0.88rem', outline: 'none' }} />
            <button onClick={add} disabled={adding} style={{ background: primary, color: '#fff', border: 'none', borderRadius: 10, padding: '10px 20px', fontWeight: 700, cursor: 'pointer', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
              {adding ? '…' : 'Add Domain'}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// src/pages/tenant/TenantTeam.jsx
export function TenantTeam() {
  const { tenant } = useTenant()
  const primary = tenant.primary_color || '#FF3D7F'
  const [team, setTeam]   = useState([])
  const [form, setForm]   = useState({ name: '', email: '', role: 'tenant_manager' })
  const [saving, setSaving] = useState(false)

  useEffect(() => { svc.getTeam().then(r => setTeam(r.data?.data ?? [])) }, [])

  const invite = async () => {
    if (!form.name || !form.email) return
    setSaving(true)
    try { await svc.inviteTeamMember(form); setForm({ name: '', email: '', role: 'tenant_manager' }); svc.getTeam().then(r => setTeam(r.data?.data ?? [])) }
    finally { setSaving(false) }
  }

  const remove = async (id) => {
    if (!confirm('Remove this team member?')) return
    await svc.removeTeamMember(id)
    setTeam(t => t.filter(m => m.id !== id))
  }

  return (
    <div style={{ maxWidth: 700 }}>
      <h1 style={{ fontFamily: 'Playfair Display, serif', fontWeight: 800, fontSize: '1.6rem', color: '#1A1A2E', margin: '0 0 6px' }}>Team</h1>
      <p style={{ color: '#9CA3AF', margin: '0 0 28px', fontSize: '0.88rem' }}>Manage who has access to your marketplace admin.</p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 24 }}>
        {team.map(m => (
          <div key={m.id} style={{ background: '#fff', borderRadius: 14, border: '1.5px solid #F0F0F0', padding: '14px 18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: '0.88rem', color: '#1A1A2E' }}>{m.name}</div>
              <div style={{ fontSize: '0.75rem', color: '#9CA3AF' }}>{m.email}</div>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <span style={{ background: `${primary}15`, color: primary, padding: '3px 10px', borderRadius: 8, fontSize: '0.72rem', fontWeight: 700 }}>{m.role}</span>
              <button onClick={() => remove(m.id)} style={{ background: 'none', border: 'none', color: '#DC2626', cursor: 'pointer', fontSize: '0.8rem' }}>Remove</button>
            </div>
          </div>
        ))}
      </div>

      <div style={{ background: '#fff', borderRadius: 14, border: '1.5px solid #F0F0F0', padding: '24px' }}>
        <div style={{ fontWeight: 700, fontSize: '0.9rem', color: '#1A1A2E', marginBottom: 16 }}>Invite Team Member</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
          {[['Name', 'name', 'Jane Doe'], ['Email', 'email', 'jane@company.com']].map(([label, key, ph]) => (
            <div key={key}>
              <label style={{ display: 'block', fontWeight: 600, fontSize: '0.78rem', color: '#6B7280', marginBottom: 6 }}>{label}</label>
              <input value={form[key]} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} placeholder={ph}
                style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #E5E7EB', borderRadius: 8, fontSize: '0.85rem', boxSizing: 'border-box', outline: 'none' }} />
            </div>
          ))}
        </div>
        <select value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}
          style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #E5E7EB', borderRadius: 8, fontSize: '0.85rem', marginBottom: 16, outline: 'none' }}>
          <option value="tenant_admin">Admin (full access)</option>
          <option value="tenant_manager">Manager (limited access)</option>
        </select>
        <button onClick={invite} disabled={saving} style={{ background: primary, color: '#fff', border: 'none', borderRadius: 10, padding: '10px 24px', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer' }}>
          {saving ? 'Inviting…' : 'Send Invite'}
        </button>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// src/pages/tenant/TenantBilling.jsx
export function TenantBilling() {
  const { tenant } = useTenant()
  const primary = tenant.primary_color || '#FF3D7F'
  const [billing, setBilling]   = useState(null)
  const [upgrading, setUpgrading] = useState(null)

  useEffect(() => { svc.getBilling().then(r => setBilling(r.data?.data ?? r.data)) }, [])

  const PLANS = [
    { key: 'starter',    label: 'Starter',    price: '₹15,000', features: ['50 suppliers', '500 products', 'Email support', 'Subdomain only'] },
    { key: 'growth',     label: 'Growth',     price: '₹45,000', features: ['500 suppliers', '5,000 products', 'Analytics', 'Custom domain', 'White-label', 'Chat support'] },
    { key: 'enterprise', label: 'Enterprise', price: '₹1,50,000', features: ['Unlimited everything', 'API access', 'Dedicated support', '4hr SLA', 'Custom contracts'] },
  ]

  const upgrade = async (plan) => {
    setUpgrading(plan)
    try {
      const r = await svc.createUpgradeOrder({ plan })
      const order = r.data?.data
      // Open Razorpay checkout
      const rzp = new window.Razorpay({
        key: import.meta.env.VITE_RAZORPAY_KEY,
        order_id: order.order_id,
        amount: order.amount,
        currency: 'INR',
        name: tenant.brand_name,
        description: `${order.plan_label} Plan — Monthly`,
        handler: async (resp) => {
          await svc.confirmPayment({ ...resp, plan })
          svc.getBilling().then(r => setBilling(r.data?.data ?? r.data))
          alert('🎉 Plan upgraded successfully!')
        },
      })
      rzp.open()
    } finally { setUpgrading(null) }
  }

  if (!billing) return <div style={{ color: '#9CA3AF', padding: 40, textAlign: 'center' }}>Loading…</div>

  return (
    <div style={{ maxWidth: 800 }}>
      <h1 style={{ fontFamily: 'Playfair Display, serif', fontWeight: 800, fontSize: '1.6rem', color: '#1A1A2E', margin: '0 0 6px' }}>Billing & Plan</h1>
      <p style={{ color: '#9CA3AF', margin: '0 0 28px', fontSize: '0.88rem' }}>Manage your subscription and view invoice history.</p>

      {/* Current plan */}
      <div style={{ background: `linear-gradient(135deg, ${primary}, ${tenant.secondary_color || '#FF8500'})`, borderRadius: 16, padding: '24px 28px', marginBottom: 28, color: '#fff' }}>
        <div style={{ fontSize: '0.78rem', fontWeight: 700, opacity: 0.8, marginBottom: 4 }}>CURRENT PLAN</div>
        <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: '1.8rem' }}>{billing.plan_config?.label || billing.plan}</div>
        <div style={{ opacity: 0.8, marginTop: 4, fontSize: '0.9rem' }}>₹{Number(billing.monthly_fee).toLocaleString()} / month · {billing.status}</div>
        {billing.plan_expires_at && <div style={{ opacity: 0.65, marginTop: 4, fontSize: '0.8rem' }}>Renews {new Date(billing.plan_expires_at).toLocaleDateString()}</div>}
      </div>

      {/* Plan cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16, marginBottom: 32 }}>
        {PLANS.map(p => {
          const isCurrent = billing.plan === p.key
          return (
            <div key={p.key} style={{ background: '#fff', borderRadius: 16, border: `2px solid ${isCurrent ? primary : '#F0F0F0'}`, padding: '20px', position: 'relative' }}>
              {isCurrent && <div style={{ position: 'absolute', top: -10, left: 20, background: primary, color: '#fff', padding: '2px 10px', borderRadius: 10, fontSize: '0.65rem', fontWeight: 700 }}>CURRENT</div>}
              <div style={{ fontWeight: 800, fontSize: '1rem', color: '#1A1A2E', marginBottom: 4 }}>{p.label}</div>
              <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: '1.4rem', color: primary, marginBottom: 12 }}>{p.price}<span style={{ fontSize: '0.75rem', color: '#9CA3AF', fontWeight: 400 }}>/mo</span></div>
              {p.features.map(f => <div key={f} style={{ fontSize: '0.78rem', color: '#6B7280', marginBottom: 4 }}>✓ {f}</div>)}
              {!isCurrent && (
                <button onClick={() => upgrade(p.key)} disabled={upgrading === p.key}
                  style={{ marginTop: 16, width: '100%', padding: '9px', background: isCurrent ? '#F0F0F0' : primary, color: '#fff', border: 'none', borderRadius: 10, fontWeight: 700, cursor: 'pointer', fontSize: '0.82rem' }}>
                  {upgrading === p.key ? '…' : `Upgrade to ${p.label}`}
                </button>
              )}
            </div>
          )
        })}
      </div>

      {/* Invoice history */}
      <h3 style={{ fontWeight: 700, fontSize: '0.9rem', color: '#1A1A2E', marginBottom: 12 }}>Invoice History</h3>
      <div style={{ background: '#fff', borderRadius: 14, border: '1.5px solid #F0F0F0', overflow: 'hidden' }}>
        {(billing.invoices || []).length === 0
          ? <div style={{ padding: '24px', textAlign: 'center', color: '#9CA3AF', fontSize: '0.85rem' }}>No invoices yet.</div>
          : (billing.invoices || []).map(inv => (
            <div key={inv.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 18px', borderBottom: '1px solid #F0F0F0' }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: '0.85rem', color: '#1A1A2E' }}>{inv.invoice_number}</div>
                <div style={{ fontSize: '0.75rem', color: '#9CA3AF' }}>{inv.period_from} → {inv.period_to}</div>
              </div>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <span style={{ fontWeight: 700, fontSize: '0.9rem', color: '#1A1A2E' }}>₹{Number(inv.amount).toLocaleString()}</span>
                <span style={{ background: inv.status === 'paid' ? '#D1FAE5' : '#FEF3C7', color: inv.status === 'paid' ? '#065F46' : '#92400E', padding: '2px 8px', borderRadius: 6, fontSize: '0.7rem', fontWeight: 700 }}>
                  {inv.status}
                </span>
              </div>
            </div>
          ))
        }
      </div>
    </div>
  )
}
